/**
 * Configuración de carga de imágenes optimizada para Hostinger
 * - Soporte para CDN
 * - Lazy loading inteligente
 * - Cache y prefetch
 * - Fallbacks robustos
 */

export const IMAGE_CONFIG = {
  // URLs base (puedes agregar CDN aquí)
  baseUrl: "https://photobook.alavuelta360.com.mx/images/",
  cdnUrl: null, // Agregar si usas CloudFlare, BunnyCDN, etc.

  // Configuración de tamaños
  sizes: {
    thumb: { width: 300, quality: 75 },
    medium: { width: 800, quality: 82 },
    large: { width: 1920, quality: 85 },
  },

  // Configuración de lazy loading
  lazyLoading: {
    rootMargin: "200px", // Cargar antes de que sea visible
    threshold: 0.01,
    enableBlur: true, // Efecto blur mientras carga
  },

  // Configuración de cache
  cache: {
    enabled: true,
    ttl: 300000, // 5 minutos
    maxSize: 50, // Máximo de imágenes en cache
  },

  // Prefetch inteligente
  prefetch: {
    enabled: true,
    limit: 10, // Máximo de imágenes a prefetch
    priority: ["medium", "thumb"], // Orden de preferencia
  },

  // Formatos soportados (en orden de preferencia)
  formats: ["webp", "jpeg"],

  // Configuración de red
  network: {
    timeout: 15000, // 15 segundos
    retries: 2,
    retryDelay: 1000,
  },
};

/**
 * Obtener URL optimizada para la imagen
 */
export function getOptimizedImageUrl(photo, size = "medium", format = "webp") {
  const baseUrl = IMAGE_CONFIG.cdnUrl || IMAGE_CONFIG.baseUrl;

  // Si la foto tiene image_sizes, usar esa estructura
  if (photo.image_sizes) {
    try {
      const sizes =
        typeof photo.image_sizes === "string"
          ? JSON.parse(photo.image_sizes)
          : photo.image_sizes;

      const sizeData = sizes[size] || sizes.medium || sizes.large;
      return sizeData?.[format] || sizeData?.jpeg || photo.url;
    } catch (e) {
      console.error("Error parsing image_sizes:", e);
    }
  }

  // Fallback a URL directa
  return photo.url;
}

/**
 * Prefetch de imágenes en segundo plano
 */
export function prefetchImages(photos, maxCount = 10) {
  // Validar que photos sea un array
  if (!Array.isArray(photos) || photos.length === 0) {
    console.warn("prefetchImages: photos no es un array válido o está vacío");
    return;
  }

  if (!IMAGE_CONFIG.prefetch.enabled) {
    return;
  }

  const imagesToPrefetch = photos.slice(0, maxCount);

  // Usar requestIdleCallback para no bloquear el thread principal
  if (typeof window !== "undefined" && "requestIdleCallback" in window) {
    window.requestIdleCallback(
      () => {
        if (!Array.isArray(imagesToPrefetch)) return;

        imagesToPrefetch.forEach((photo) => {
          if (!photo || typeof photo !== "object") return;

          IMAGE_CONFIG.prefetch.priority.forEach((size) => {
            try {
              const url = getOptimizedImageUrl(photo, size, "webp");
              if (url) {
                const link = document.createElement("link");
                link.rel = "prefetch";
                link.as = "image";
                link.href = url;
                document.head.appendChild(link);
              }
            } catch (err) {
              console.warn("Error prefetching image:", err);
            }
          });
        });
      },
      { timeout: 2000 },
    );
  }
}

/**
 * Cargar imagen con reintentos
 */
export async function loadImageWithRetry(
  url,
  retries = IMAGE_CONFIG.network.retries,
) {
  return new Promise((resolve, reject) => {
    let attempts = 0;

    const attemptLoad = () => {
      const img = new Image();
      const timeout = setTimeout(() => {
        img.src = "";
        if (attempts < retries) {
          attempts++;
          console.log(
            `⚠️ Reintentando carga de imagen (${attempts}/${retries})`,
          );
          setTimeout(attemptLoad, IMAGE_CONFIG.network.retryDelay * attempts);
        } else {
          reject(new Error("Timeout loading image"));
        }
      }, IMAGE_CONFIG.network.timeout);

      img.onload = () => {
        clearTimeout(timeout);
        resolve(img);
      };

      img.onerror = () => {
        clearTimeout(timeout);
        if (attempts < retries) {
          attempts++;
          console.log(
            `⚠️ Reintentando carga de imagen (${attempts}/${retries})`,
          );
          setTimeout(attemptLoad, IMAGE_CONFIG.network.retryDelay * attempts);
        } else {
          reject(new Error("Error loading image"));
        }
      };

      img.src = url;
    };

    attemptLoad();
  });
}

/**
 * Optimizar orden de carga de imágenes
 * Prioriza las imágenes visibles en viewport
 */
export function prioritizeImageLoading(photos, viewportPhotos) {
  const visibleIds = new Set(viewportPhotos.map((p) => p.id));

  return photos.sort((a, b) => {
    const aVisible = visibleIds.has(a.id);
    const bVisible = visibleIds.has(b.id);

    if (aVisible && !bVisible) return -1;
    if (!aVisible && bVisible) return 1;
    return 0;
  });
}

/**
 * Detectar soporte de WebP con caching
 */
let webpSupport = null;
export function supportsWebP() {
  if (webpSupport !== null) return webpSupport;
  if (typeof document === "undefined") return false;

  try {
    const elem = document.createElement("canvas");
    if (elem.getContext && elem.getContext("2d")) {
      webpSupport =
        elem.toDataURL("image/webp").indexOf("data:image/webp") === 0;
      return webpSupport;
    }
  } catch (e) {
    console.warn("Error detectando soporte WebP:", e);
  }
  webpSupport = false;
  return false;
}

/**
 * Monitorear velocidad de conexión y ajustar calidad
 * Compatible con todos los navegadores móviles
 */
export function getOptimalQuality() {
  if (typeof navigator === "undefined") return "medium";

  // Intentar obtener info de conexión (no soportado en iOS Safari)
  try {
    const connection =
      navigator.connection ||
      navigator.mozConnection ||
      navigator.webkitConnection;

    if (connection) {
      // Slow 2G o Save-Data activado
      if (connection.saveData || connection.effectiveType === "slow-2g") {
        return "thumb";
      }
      // 2G o 3G
      if (
        connection.effectiveType === "2g" ||
        connection.effectiveType === "3g"
      ) {
        return "medium";
      }
    }
  } catch (e) {
    // Ignorar errores en navegadores que no soportan Connection API
  }

  // Fallback: detectar por user agent si es dispositivo antiguo
  const ua = navigator.userAgent.toLowerCase();
  if (ua.includes("android 4") || ua.includes("android 5")) {
    return "medium"; // Android antiguo
  }

  // Por defecto, calidad media-alta (equilibrio)
  return "medium";
}
